<template>
  <div class="shared-bar">
    <div class="bar-bg">
      <div
        class="bar-fill"
        :style="{ width: `${progressPercent}%` }"
        :class="{ 'bar-won': won }"
      ></div>
      <div class="bar-glow" :style="{ width: `${progressPercent}%` }"></div>
    </div>
    <div class="bar-info">
      <div class="flex items-center gap-2">
        <span class="text-white/50 text-[10px] uppercase tracking-wider">Tisch-Score</span>
        <span v-if="combo > 2" class="combo-badge">{{ combo }}x Combo!</span>
      </div>
      <span class="text-white font-mono font-bold text-sm">
        {{ totalScore }} / {{ target }}
      </span>
    </div>
    <!-- Player dots -->
    <div v-if="players.length > 1" class="player-dots">
      <div
        v-for="(p, i) in players"
        :key="p.id"
        class="player-dot"
        :class="{ 'dot-active': p.score > 0 }"
        :style="{ backgroundColor: playerColors[i % playerColors.length] }"
        :title="`${p.name}: ${p.score}`"
      >
        <span class="text-[8px]">{{ p.score }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  totalScore: { type: Number, default: 0 },
  target: { type: Number, default: 100 },
  players: { type: Array, default: () => [] },
  combo: { type: Number, default: 0 },
  won: { type: Boolean, default: false },
})

const playerColors = ['#7C3AED', '#06B6D4', '#F59E0B', '#10B981', '#EF4444', '#EC4899']
const progressPercent = computed(() => Math.min(100, (props.totalScore / Math.max(1, props.target)) * 100))
</script>

<style scoped>
.shared-bar {
  @apply w-full px-4 py-2;
  background: linear-gradient(180deg, rgba(10,10,20,0.95) 0%, rgba(10,10,20,0.8) 100%);
}
.bar-bg {
  @apply relative h-2.5 rounded-full overflow-hidden;
  background: rgba(255,255,255,0.06);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.4);
}
.bar-fill {
  @apply absolute inset-y-0 left-0 rounded-full transition-all duration-300;
  background: linear-gradient(90deg, #7C3AED, #06B6D4, #10B981);
  box-shadow: 0 0 12px rgba(124,58,237,0.4);
}
.bar-won {
  background: linear-gradient(90deg, #FFD700, #FFA500) !important;
  box-shadow: 0 0 20px rgba(255,215,0,0.6) !important;
  animation: wonPulse 0.5s ease infinite alternate;
}
.bar-glow {
  @apply absolute inset-y-0 left-0 rounded-full pointer-events-none;
  background: linear-gradient(90deg, transparent 60%, rgba(255,255,255,0.15));
  filter: blur(2px);
}
.bar-info {
  @apply flex items-center justify-between mt-1.5;
}
.combo-badge {
  @apply text-[10px] font-bold text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded-full;
  animation: comboPop 0.3s ease;
}
.player-dots {
  @apply flex gap-1.5 mt-1;
}
.player-dot {
  @apply w-5 h-5 rounded-full flex items-center justify-center text-white font-bold opacity-40 transition-opacity duration-300;
}
.dot-active {
  @apply opacity-100;
  box-shadow: 0 0 6px currentColor;
}
@keyframes wonPulse {
  to { box-shadow: 0 0 30px rgba(255,215,0,0.8); }
}
@keyframes comboPop {
  0% { transform: scale(0.5); }
  100% { transform: scale(1); }
}
</style>
